import { Component, DestroyRef, inject, Input, OnChanges, SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormGroup } from '@angular/forms';
import { GiftUnlockExtension } from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import { Subscription } from 'rxjs';
import { MAXX_AI_RANGE_INVALID, MAXX_AI_RANGE_REQUIRED } from '../../gift-unlock.validators';

const RECOMMENDED_MIN_RANGE = 5;

@Component({
  selector: 'gift-unlock-guardrails',
  templateUrl: './gift-unlock-guardrails.component.html',
  styleUrls: ['./gift-unlock-guardrails.component.scss'],
  standalone: false,
})
export class GiftUnlockGuardrailsComponent implements OnChanges {
  @Input() formGroup!: UntypedFormGroup;
  @Input() couponType!: string;
  @Input() shopCurrencySymbol!: string;

  isPercentage = false;
  symbol = '';
  maxBound: number | undefined;
  rangeErrorKind: 'required' | 'invalid' | null = null;
  rangeErrorMessage: string | null = null;
  showRecommendation = false;

  private destroyRef = inject(DestroyRef);
  private statusSub?: Subscription;

  ngOnChanges(changes: SimpleChanges): void {
    this.isPercentage = this.couponType === GiftUnlockExtension.CouponType.PERCENTAGE;
    this.symbol = this.isPercentage ? '%' : this.shopCurrencySymbol;
    this.maxBound = this.isPercentage ? 100 : undefined;

    if (changes['formGroup']) {
      this.statusSub?.unsubscribe();
      this.statusSub = this.formGroup?.statusChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.updateRangeState());
    }

    this.updateRangeState();
  }

  private updateRangeState(): void {
    const errors = this.formGroup?.errors;
    const unit = this.isPercentage ? 'percentage' : 'amount';

    if (errors?.[MAXX_AI_RANGE_INVALID]) {
      this.rangeErrorKind = 'invalid';
      this.rangeErrorMessage = `Max ${unit} must be greater than Min.`;
    } else if (errors?.[MAXX_AI_RANGE_REQUIRED]) {
      this.rangeErrorKind = 'required';
      this.rangeErrorMessage = `Set both a Min and Max ${unit}.`;
    } else {
      this.rangeErrorKind = null;
      this.rangeErrorMessage = null;
    }

    this.showRecommendation = this.rangeErrorKind === null && this.isRangeNarrow();
  }

  private isRangeNarrow(): boolean {
    const min = this.formGroup?.get('min')?.value;
    const max = this.formGroup?.get('max')?.value;
    if (min == null || min === '' || max == null || max === '') return false;
    return Number(max) - Number(min) <= RECOMMENDED_MIN_RANGE;
  }
}
